import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, Pencil, Eye, Power, Trash, Car, Package, CurrencyEur, TrendUp } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProUsers = () => {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPro, setEditPro] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', company_name: '', contact_name: '', phone: '' });

  const fetchPros = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/pro-users`);
      setPros(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchPros(); }, []);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.company_name) {
      toast.error('Email, mot de passe et nom d\'entreprise requis');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/pro-users`, form);
      toast.success(`Compte pro créé pour ${form.company_name}`);
      setForm({ email: '', password: '', company_name: '', contact_name: '', phone: '' });
      setShowCreate(false);
      fetchPros();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdate = async () => {
    if (!editPro) return;
    try {
      const data = { company_name: form.company_name, contact_name: form.contact_name, phone: form.phone };
      if (form.password) data.password = form.password;
      await axios.put(`${API_URL}/api/admin/pro-users/${editPro.id}`, data);
      toast.success('Compte mis à jour');
      setEditPro(null);
      fetchPros();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const toggleActive = async (pro) => {
    try {
      await axios.put(`${API_URL}/api/admin/pro-users/${pro.id}`, { active: !pro.active });
      toast.success(pro.active ? 'Compte désactivé' : 'Compte activé');
      fetchPros();
    } catch (e) { toast.error('Erreur'); }
  };

  const deletePro = async (pro) => {
    if (!window.confirm(`Supprimer le compte de ${pro.company_name} ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/pro-users/${pro.id}`);
      toast.success('Compte supprimé');
      fetchPros();
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <AdminLayout title="Gestion des Pros"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout title="Gestion des Pros">
      {/* Create / Edit Modal */}
      {(showCreate || editPro) && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => { setShowCreate(false); setEditPro(null); }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-6">
              {editPro ? `Modifier: ${editPro.company_name}` : 'Créer un compte pro'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-600 text-sm mb-1">Nom de l'entreprise *</label>
                <input type="text" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="pro-company" />
              </div>
              {!editPro && (
                <div>
                  <label className="block text-gray-600 text-sm mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="pro-email" />
                </div>
              )}
              <div>
                <label className="block text-gray-600 text-sm mb-1">{editPro ? 'Nouveau mot de passe (laisser vide si inchangé)' : 'Mot de passe *'}</label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="pro-password" />
              </div>
              <div>
                <label className="block text-gray-600 text-sm mb-1">Nom du contact</label>
                <input type="text" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="pro-contact" />
              </div>
              <div>
                <label className="block text-gray-600 text-sm mb-1">Téléphone</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full border border-gray-300 p-3 text-sm focus:border-[#E63946] outline-none" data-testid="pro-phone" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowCreate(false); setEditPro(null); }} className="flex-1 py-3 border border-gray-300 text-gray-600 text-sm font-semibold">Annuler</button>
              <button onClick={editPro ? handleUpdate : handleCreate} className="flex-1 py-3 bg-[#E63946] text-white text-sm font-semibold hover:bg-[#D90429]" data-testid="pro-submit">
                {editPro ? 'Enregistrer' : 'Créer le compte'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500 text-sm">{pros.length} compte{pros.length > 1 ? 's' : ''} pro</p>
        <button
          onClick={() => { setForm({ email: '', password: '', company_name: '', contact_name: '', phone: '' }); setShowCreate(true); }}
          className="flex items-center gap-2 bg-[#E63946] text-white px-5 py-3 text-sm font-bold uppercase hover:bg-[#D90429]"
          data-testid="create-pro-btn"
        >
          <UserPlus size={18} weight="bold" /> Créer un compte pro
        </button>
      </div>

      {/* Pro Users List */}
      <div className="bg-white border border-gray-200 shadow-sm">
        <div className="divide-y divide-gray-100">
          {pros.length > 0 ? pros.map((pro) => (
            <div key={pro.id} className={`p-5 ${!pro.active ? 'opacity-50 bg-gray-50' : ''}`} data-testid={`pro-row-${pro.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#E63946]/10 flex items-center justify-center flex-shrink-0 rounded-lg">
                  <span className="text-[#E63946] font-bold text-lg">{(pro.company_name || 'P')[0].toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="text-gray-900 font-semibold">{pro.company_name}</p>
                    {!pro.active && <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5">DÉSACTIVÉ</span>}
                  </div>
                  <p className="text-gray-400 text-sm">{pro.email} {pro.phone && `• ${pro.phone}`}</p>
                  {pro.contact_name && <p className="text-gray-400 text-xs mt-0.5">Contact: {pro.contact_name}</p>}
                </div>

                {/* Activity stats */}
                <div className="hidden lg:flex items-center gap-6">
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-gray-400"><Car size={14} /></div>
                    <p className="text-gray-900 font-bold text-sm">{pro.stats?.vehicles || 0}</p>
                    <p className="text-gray-400 text-[10px]">Véhicules</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-gray-400"><Package size={14} /></div>
                    <p className="text-gray-900 font-bold text-sm">{pro.stats?.parts_available || 0}</p>
                    <p className="text-gray-400 text-[10px]">Pièces dispo</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-gray-400"><TrendUp size={14} /></div>
                    <p className="text-green-600 font-bold text-sm">{pro.stats?.parts_sold || 0}</p>
                    <p className="text-gray-400 text-[10px]">Vendues</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1 text-gray-400"><CurrencyEur size={14} /></div>
                    <p className="text-[#2A9D8F] font-bold text-sm">{(pro.stats?.stock_value || 0).toLocaleString('fr-FR')} €</p>
                    <p className="text-gray-400 text-[10px]">Valeur stock</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => toggleActive(pro)}
                    className={`p-2 ${pro.active ? 'text-green-600 hover:bg-green-50' : 'text-red-500 hover:bg-red-50'}`}
                    title={pro.active ? 'Désactiver' : 'Activer'}
                    data-testid={`toggle-pro-${pro.id}`}
                  >
                    <Power size={18} weight="bold" />
                  </button>
                  <button
                    onClick={() => { setEditPro(pro); setForm({ email: pro.email, password: '', company_name: pro.company_name, contact_name: pro.contact_name || '', phone: pro.phone || '' }); }}
                    className="p-2 text-gray-400 hover:text-[#E63946] hover:bg-gray-50"
                    data-testid={`edit-pro-${pro.id}`}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => deletePro(pro)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-pro-${pro.id}`}
                  >
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            </div>
          )) : (
            <div className="p-12 text-center">
              <UserPlus size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">Aucun compte professionnel</p>
              <p className="text-gray-400 text-sm">Créez votre premier compte pro pour commencer</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProUsers;
